import { Box, Button, Link, Rating, TextField, Typography } from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSubmitFreelancerOnboardingExperienceRating } from "../../../hooks/useFreelancerOnboarding";
import { zoullooColors } from "../../../theme";
import { FreelancerOnboarding } from "../../../models/FreelancerOnboarding";

interface FreelancerOnboardingExperienceRatingProps {
	freelancerOnboarding: FreelancerOnboarding;
}

const FreelancerOnboardingExperienceRating: React.FC<
	FreelancerOnboardingExperienceRatingProps
> = ({ freelancerOnboarding }) => {
	const { t } = useTranslation();
	const [rating, setRating] = useState<number | null>(null);
	const [feedback, setFeedback] = useState<string>("");
	const [isSubmitted, setIsSubmitted] = useState<boolean>(false);

	const { mutate: submitRating } = useSubmitFreelancerOnboardingExperienceRating();

	const handleSubmit = () => {
		if (rating === null) return;

		submitRating(
			{
				freelancerOnboardingId: freelancerOnboarding.id,
				rating,
				feedback
			},
			{
				onSuccess: () => {
					setIsSubmitted(true);
				}
			}
		);
	};

	const handleCancel = () => {
		if (rating === null) return;

		// Submit rating without feedback
		submitRating(
			{
				freelancerOnboardingId: freelancerOnboarding.id,
				rating
			},
			{
				onSuccess: () => {
					setIsSubmitted(true);
				}
			}
		);
	};

	if (isSubmitted || freelancerOnboarding.freelancerOnboardingExperienceRatingId) {
		return (
			<Box>
				<Typography variant="h2" gutterBottom>
					{t("freelancerApplication.accepted.title")}
				</Typography>

				<Typography variant="h6" sx={{ textAlign: "center", my: 3, fontWeight: "bold" }}>
					{t("freelancerApplication.accepted.submitted.thankYou")}
				</Typography>

				<Typography variant="body1" sx={{ textAlign: "center", mb: 3, whiteSpace: "pre-line" }}>
					{t("freelancerApplication.accepted.submitted.feedbackValue")}
				</Typography>

				<Typography variant="body1" lineHeight={3.5}>
					{t("freelancerApplication.accepted.submitted.contactMessage")}{" "}
					<Link
						href="mailto:hello_talent@zoulloo.com"
						sx={{
							color: zoullooColors.secondary.main,
							fontWeight: "500",
							textDecoration: "none",
							cursor: "pointer",
							"&:hover": {
								textDecoration: "underline"
							}
						}}
					>
						hello_talent@zoulloo.com
					</Link>
				</Typography>

				<Typography variant="body1" lineHeight={3.5}>
					{t("freelancerApplication.accepted.submitted.trackJourney")}
				</Typography>

				<Typography variant="body1" lineHeight={3.5}>
					{t("freelancerApplication.accepted.submitted.closingAdventure")}
				</Typography>

				<Typography variant="body1" lineHeight={3.5}>
					{t("freelancerApplication.accepted.submitted.signature")}
				</Typography>
			</Box>
		);
	}

	return (
		<Box>
			<Typography variant="h2" gutterBottom>
				{t("freelancerApplication.accepted.title")}
			</Typography>

			<Typography variant="body1" sx={{ whiteSpace: "pre-line", mb: 3 }}>
				{t("freelancerApplication.accepted.congratulations")}
			</Typography>

			<Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
				{t("freelancerApplication.accepted.ratingTitle")}
			</Typography>

			<Box display={"flex"} alignItems="center" gap={2} sx={{ my: 2 }}>
				<Typography variant="body1" component={"span"}>
					{t("freelancerApplication.accepted.ratingLabel")}
				</Typography>
				<Rating
					name="onboarding-experience-rating"
					value={rating}
					onChange={(_event, newValue) => {
						setRating(newValue);
					}}
					size="large"
					sx={{
						"& .MuiRating-iconFilled": {
							color: zoullooColors.secondary.main
						}
					}}
				/>
			</Box>

			{rating !== null && (
				<Box sx={{ mb: 3 }}>
					<Typography variant="h6" gutterBottom>
						{t("freelancerApplication.accepted.improvementTitle")}
					</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
						{t("freelancerApplication.accepted.improvementDescription")}
					</Typography>
					<TextField
						fullWidth
						multiline
						rows={4}
						value={feedback}
						onChange={e => setFeedback(e.target.value)}
						placeholder={t("freelancerApplication.accepted.feedbackPlaceholder")}
						variant="outlined"
						slotProps={{
							htmlInput: {
								maxLength: 1500
							}
						}}
						sx={{
							mb: 2,
							"& .MuiInputBase-root textarea": {
								resize: "vertical"
							}
						}}
					/>
					<Box display="flex" gap={2} justifyContent="flex-end">
						<Button
							disableRipple
							variant="outlined"
							onClick={handleCancel}
							sx={{
								borderColor: zoullooColors.secondary.main,
								color: zoullooColors.secondary.main
							}}
						>
							{t("freelancerApplication.accepted.cancel")}
						</Button>
						<Button
							disableRipple
							variant="contained"
							onClick={handleSubmit}
							disabled={feedback.length > 1500}
							sx={{
								backgroundColor: zoullooColors.secondary.main
							}}
						>
							{t("freelancerApplication.accepted.submit")}
						</Button>
					</Box>
				</Box>
			)}

			<Typography variant="body1" lineHeight={3.5}>
				{t("freelancerApplication.accepted.contactMessage")}{" "}
				<Link
					href="mailto:hello_talent@zoulloo.com"
					sx={{
						color: zoullooColors.secondary.main,
						fontWeight: "500",
						textDecoration: "none",
						cursor: "pointer",
						"&:hover": {
							textDecoration: "underline"
						}
					}}
				>
					hello_talent@zoulloo.com
				</Link>
			</Typography>

			<Typography variant="body1" lineHeight={3.5}>
				{t("freelancerApplication.accepted.trackJourney")}
			</Typography>

			<Typography variant="body1" lineHeight={3.5}>
				{t("freelancerApplication.accepted.closing")}
			</Typography>

			<Typography variant="body1" lineHeight={3.5}>
				{t("freelancerApplication.accepted.signature")}
			</Typography>
		</Box>
	);
};

export default FreelancerOnboardingExperienceRating;
